import { fetchPrefectureAndMunicipalityByZipCodeApi } from "@/api/services/global/constant";
import { TGetProvincesAndMunicipalitiesResDto } from "@/api/services/global/constant/dtos/get-provinces-municipalities.dto";
import { PATTERN } from "@/constants/pattern";
import { convertZipCodeToNumber } from "@/utils/input";
import { convertFullWidthToHalfWidth } from "@/utils/pieces";
import { useEffect, useState } from "react";
import { FieldValues, Path, PathValue, UseFormReturn, useWatch } from "react-hook-form";

export type TUseLocationInfoProps<T extends FieldValues> = {
  names: {
    zipCode: Path<T>;
    province: Path<T>;
    municipality?: Path<T>;
    address?: Path<T>;
  };
  methods: UseFormReturn<T, object, T>;
};

export const useLocationInfo = <T extends FieldValues>({ names, methods }: TUseLocationInfoProps<T>) => {
  const { setValue } = methods;
  const zipCodeField = useWatch({ control: methods.control, name: names.zipCode });
  const [locationInfo, setLocationInfo] = useState<TGetProvincesAndMunicipalitiesResDto | undefined>(undefined);

  const dirtyFields = methods.formState.dirtyFields as { [key: string]: boolean };
  const dirtyZipCodeField = dirtyFields[names.zipCode];
  const [previousZipCode, setPreviousZipCode] = useState<string | undefined>();

  const fetchLocationInfo = async (zipCode: string) => {
    if (!zipCode) return;
    if (!/^\d{7}$/.test(zipCode)) return;
    try {
      const rs = await fetchPrefectureAndMunicipalityByZipCodeApi(zipCode);
      setLocationInfo(rs);
      setValue(names.province, rs.prefecture as PathValue<T, Path<T>>);
      methods.trigger(names.province);
      if (names.municipality) {
        const currentMunicipality = methods.getValues(names.municipality);
        const hasCities = rs.cities && Array.isArray(rs.cities) && rs.cities.length > 0;
        const currentCityInCities = hasCities && currentMunicipality && rs.cities?.includes(currentMunicipality as string);

        if (!currentMunicipality || !currentCityInCities || dirtyZipCodeField) {
          setValue(names.municipality, `${rs.city}` as PathValue<T, Path<T>>);
          methods.trigger(names.municipality);
        }
      }
      const dirtyAnyZipCode = hasAnyNestedZipCodeDirty(dirtyFields);
      if (names.address && (dirtyZipCodeField || dirtyAnyZipCode)) {
        setValue(names.address, `${rs?.suburb}` as PathValue<T, Path<T>>);
        methods.trigger(names.address);
      }
    } catch (err) {
      setValue(names.province, "" as PathValue<T, Path<T>>);
      methods.trigger(names.province);
      if (names.municipality) {
        setValue(names.municipality, "" as PathValue<T, Path<T>>);
        methods.trigger(names.municipality);
      }
      const dirtyAnyZipCode = hasAnyNestedZipCodeDirty(dirtyFields);
      if (names.address && (dirtyZipCodeField || dirtyAnyZipCode)) {
        setValue(names.address, "" as PathValue<T, Path<T>>);
        methods.trigger(names.address);
      }
    }
  };

  const resetLocationFields = (includeAddress: boolean = false) => {
    setValue(names.province, "" as PathValue<T, Path<T>>);
    methods.trigger(names.province);

    if (names.municipality) {
      setValue(names.municipality, "" as PathValue<T, Path<T>>);
      methods.trigger(names.municipality);
    }

    if (names.address && includeAddress) {
      setValue(names.address, "" as PathValue<T, Path<T>>);
      methods.trigger(names.address);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      const zipCode = convertZipCodeToNumber(convertFullWidthToHalfWidth(zipCodeField));
      const isValidZipCode = zipCode && zipCode.length === 7 && PATTERN.ZIP_CODE.test(zipCode);

      if (isValidZipCode && zipCode !== previousZipCode) {
        setPreviousZipCode(zipCode);
        fetchLocationInfo(zipCode);
      } else if (!zipCodeField || zipCodeField.length === 0) {
        if (previousZipCode) {
          setPreviousZipCode(undefined);
          resetLocationFields(true);
        } else {
          setPreviousZipCode(undefined);
        }
      } else if (!isValidZipCode) {
        if (previousZipCode) {
          setPreviousZipCode(undefined);
          resetLocationFields(false);
        } else if (zipCode) {
          resetLocationFields(false);
        }
      }
    }, 100);

    return () => clearTimeout(handler);
  }, [zipCodeField]);

  return locationInfo;
};

const hasAnyNestedZipCodeDirty = (dirtyFields: { [s: string]: unknown } | ArrayLike<unknown>) => {
  return Object.values(dirtyFields).some((value) => {
    if (Array.isArray(value)) {
      return value.some((item) => item?.zipCode);
    }
    return false;
  });
};
